import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { 
  Menu, 
  X, 
  ChevronDown,
  Facebook,
  Phone,
  Mail,
  MapPin
} from "lucide-react";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import CookieConsent from "./CookieConsent";
import { navProductGroups, navReferenceItems } from "../data/content";

const navItems = [
  { name: "Kezdőlap", path: "/" },
  { 
    name: "Termékek", 
    path: "/termekek",
    megamenu: true,
  },
  { 
    name: "Referenciák", 
    path: "/referenciak",
    submenu: navReferenceItems
  },
  { name: "Blog", path: "/blog" },
  { name: "Kapcsolat", path: "/kapcsolat" },
];

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSubmenu(null);
    setMobileExpanded(null);
    
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 shadow-sm' : 'bg-white/90'} backdrop-blur-md border-b border-stone-200/50`} data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-24 sm:h-28 relative">
            {/* Logo - centered on mobile, left on desktop */}
            <Link to="/" className="absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0">
              <img 
                src="/logo.png" 
                alt="BRS Terasz" 
                className="h-20 sm:h-24 w-auto"
                data-testid="header-logo"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <div 
                  key={item.name} 
                  className="relative group"
                  onMouseEnter={() => (item.submenu || item.megamenu) && setOpenSubmenu(item.name)}
                  onMouseLeave={() => setOpenSubmenu(null)}
                >
                  <Link 
                    to={item.path}
                    className={`flex items-center px-4 py-2 text-sm font-medium transition-colors ${
                      location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                        ? 'text-amber-600'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {item.name}
                    {(item.submenu || item.megamenu) && <ChevronDown className="ml-1 w-4 h-4" />}
                  </Link>
                  
                  {/* Regular Submenu */}
                  {item.submenu && openSubmenu === item.name && (
                    <div className="absolute top-full left-0 mt-0 w-52 bg-white border border-stone-200 shadow-lg py-2 z-50">
                      {item.submenu.map((subitem) => (
                        <Link
                          key={subitem.name}
                          to={subitem.path}
                          className="block px-4 py-2 text-sm text-slate-600 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                        >
                          {subitem.name}
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Mega Menu for Termékek */}
                  {item.megamenu && openSubmenu === item.name && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0 bg-white border border-stone-200 shadow-lg py-4 px-6 z-50 min-w-[520px]">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                        {navProductGroups.map((group) => (
                          <div key={group.name} className="mb-3">
                            {group.items.length > 0 ? (
                              <>
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1 px-2">
                                  {group.name}
                                </p>
                                {group.items.map((subitem) => (
                                  <Link
                                    key={subitem.name}
                                    to={subitem.path}
                                    className="block px-2 py-1.5 text-sm text-slate-600 hover:bg-amber-50 hover:text-amber-600 transition-colors rounded"
                                  >
                                    {subitem.name}
                                  </Link>
                                ))}
                              </>
                            ) : (
                              <Link
                                to={group.path}
                                className="block px-2 py-1.5 text-sm font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-600 transition-colors rounded"
                              >
                                {group.name}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Social Links + CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61578531089510" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-amber-600 transition-colors"
                data-testid="header-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <Button 
                asChild
                className="rounded-none px-6 py-5 bg-amber-600 text-white hover:bg-amber-700 font-medium text-xs tracking-wider uppercase"
              >
                <Link to="/arajanlat-keres">Ajánlatot kérek</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 ml-auto"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-stone-100 py-4 shadow-lg max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            <div className="px-4 space-y-1">
              {navItems.map((item) => (
                <div key={item.name}>
                  {item.megamenu ? (
                    <>
                      <button 
                        onClick={() => setMobileExpanded(mobileExpanded === item.name ? null : item.name)}
                        className={`flex items-center justify-between w-full text-left py-3 px-3 rounded-lg font-semibold text-base ${
                          location.pathname.startsWith(item.path)
                            ? 'text-amber-600 bg-amber-50' 
                            : 'text-slate-900 hover:bg-stone-50'
                        }`}
                      >
                        {item.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === item.name ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileExpanded === item.name && (
                        <div className="pl-3 space-y-1 mt-1 mb-2 border-l-2 border-stone-200 ml-3">
                          <Link
                            to="/termekek"
                            className="block py-2 px-3 text-sm font-medium text-amber-600 hover:bg-amber-50 rounded"
                          >
                            Összes termék
                          </Link>
                          {navProductGroups.map((group) => (
                            <div key={group.name}>
                              {group.items.length > 0 ? (
                                <>
                                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mt-2 mb-1 px-3">
                                    {group.name}
                                  </p>
                                  {group.items.map((subitem) => (
                                    <Link
                                      key={subitem.name}
                                      to={subitem.path}
                                      className="block py-2 px-3 text-sm text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded"
                                    >
                                      {subitem.name}
                                    </Link>
                                  ))}
                                </>
                              ) : (
                                <Link
                                  to={group.path}
                                  className="block py-2 px-3 text-sm text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded"
                                >
                                  {group.name}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {item.submenu ? (
                        <>
                          <button 
                            onClick={() => setMobileExpanded(mobileExpanded === item.name ? null : item.name)}
                            className={`flex items-center justify-between w-full text-left py-3 px-3 rounded-lg font-semibold text-base ${
                              location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                                ? 'text-amber-600 bg-amber-50' 
                                : 'text-slate-900 hover:bg-stone-50'
                            }`}
                          >
                            {item.name}
                            <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === item.name ? 'rotate-180' : ''}`} />
                          </button>
                          {mobileExpanded === item.name && (
                            <div className="pl-3 space-y-1 mt-1 mb-2 border-l-2 border-stone-200 ml-3">
                              <Link
                                to={item.path}
                                className="block py-2 px-3 text-sm font-medium text-amber-600 hover:bg-amber-50 rounded"
                              >
                                Összes
                              </Link>
                              {item.submenu.map((subitem) => (
                                <Link
                                  key={subitem.name}
                                  to={subitem.path}
                                  className="block py-2 px-3 text-sm text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded"
                                >
                                  {subitem.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link 
                          to={item.path}
                          className={`block w-full text-left py-3 px-3 rounded-lg font-semibold text-base ${
                            location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                              ? 'text-amber-600 bg-amber-50' 
                              : 'text-slate-900 hover:bg-stone-50'
                          }`}
                        >
                          {item.name}
                        </Link>
                      )}
                    </>
                  )}
                </div>
              ))}
              
              {/* Mobile Social Links */}
              <div className="flex items-center space-x-4 pt-4 px-2">
                <a 
                  href="https://www.facebook.com/profile.php?id=61578531089510" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-amber-100 hover:text-amber-600 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
              
              <div className="pt-3">
                <Button 
                  asChild
                  className="w-full rounded-none py-4 bg-amber-600 text-white hover:bg-amber-700 font-medium text-xs tracking-wider uppercase"
                >
                  <Link to="/arajanlat-keres">Ajánlatot kérek</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-24 sm:pt-28">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="py-12 sm:py-16 bg-slate-900 text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
            {/* Logo & Description */}
            <div>
              <p className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="text-white">BRS</span> <span className="text-amber-500">Terasz</span>
              </p>
              <p className="text-slate-400 text-sm">
                Alumínium teraszok, teraszfedések, árnyékolók és kocsibeállók kivitelezése <strong className="text-slate-300">Magyarország teljes területén</strong>.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Gyors linkek</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/" className="hover:text-amber-400 transition-colors">Kezdőlap</Link></li>
                <li><Link to="/termekek" className="hover:text-amber-400 transition-colors">Termékek</Link></li>
                <li><Link to="/referenciak" className="hover:text-amber-400 transition-colors">Referenciák</Link></li>
                <li><Link to="/blog" className="hover:text-amber-400 transition-colors">Blog</Link></li>
                <li><Link to="/kapcsolat" className="hover:text-amber-400 transition-colors">Kapcsolat</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Kapcsolat</h4>
              <ul className="space-y-3 text-slate-400 text-sm">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-amber-500" />
                  <a href="tel:+36703379805" className="hover:text-amber-400 transition-colors">+36 70 337 9805</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-amber-500" />
                  <a href="mailto:info@brsterasz.hu" className="hover:text-amber-400 transition-colors">info@brsterasz.hu</a>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <a href="https://www.google.com/maps?q=47.74872,18.07261" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">Komárom, Móra Ferenc utca 4</a>
                </li>
              </ul>
            </div>

            {/* Social */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Kövessen minket</h4>
              <div className="flex space-x-3">
                <a 
                  href="https://www.facebook.com/profile.php?id=61578531089510" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 flex items-center justify-center hover:bg-amber-500 transition-colors"
                  data-testid="footer-facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <Separator className="my-8 sm:my-12 bg-slate-800" />

          <div className="flex flex-col sm:flex-row justify-between items-center text-slate-400 text-sm gap-4">
            <p>&copy; {new Date().getFullYear()} BRS Terasz. Minden jog fenntartva.</p>
            <Link to="/adatkezelesi-tajekoztato" className="hover:text-amber-400 transition-colors text-sm">
              Adatkezelési tájékoztató
            </Link>
          </div>
        </div>
      </footer>

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
};

export default Layout;
